import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  // Check if token expires in the next 10 seconds (buffer time)
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime + 10;
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and validate it
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token is expired before making the request
      if (isTokenExpired(token)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error('Tu sesión ha expirado. Por favor, inicia sesión de nuevo.');
        window.location.href = '/login';
        return Promise.reject(new Error('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      // Unauthorized - clear token and redirect to login (except on login itself)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      toast.error('Tu sesión ha expirado. Por favor, inicia sesión de nuevo.');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    // Check if token is expired
    if (isTokenExpired(token)) {
      // Clean up expired token
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return false;
    }

    return true;
  },

  // Export helper functions for external use
  isTokenExpired: (token) => isTokenExpired(token),
  decodeToken: (token) => decodeToken(token)
};

// Calculator services
export const calculatorService = {
  getProducts: async () => {
    const response = await api.get('/public/calculator/products');
    return response.data;
  },

  calculate: async (calculationData) => {
    // Use the authenticated endpoint if clientId is provided
    // The authenticated endpoint returns client information
    if (calculationData.clientId) {
      const response = await api.post('/calculator/calculate', calculationData);
      return response.data;
    } else {
      // Use public endpoint for calculations without client
      const response = await api.post('/public/calculator/calculate', calculationData);
      return response.data;
    }
  },

  getClients: async () => {
    const response = await api.get('/public/calculator/clients');
    return response.data;
  },

  saveOrder: async (orderData) => {
    const response = await api.post('/calculator/save-order', orderData);
    return response.data;
  }
};

// Client services
export const clientService = {
  getAll: async () => {
    const response = await api.get('/clients');
    return response.data.clients || [];
  },

  getById: async (id) => {
    const response = await api.get(`/clients/${id}`);
    return response.data.client || response.data;
  },

  update: async (id, clientData) => {
    const response = await api.put(`/clients/${id}`, clientData);
    return response.data.client || response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/clients/${id}`);
    return response.data;
  }
};

// Order services
export const orderService = {
  getAll: async (params) => {
    const response = await api.get('/orders', { params });
    return { orders: response.data.orders || [], total: response.data.total || 0 };
  },

  getById: async (id) => {
    const response = await api.get(`/orders/${id}`);
    return response.data.order || response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.patch(`/orders/${id}/status`, { status });
    return response.data;
  },

  syncOdoo: async (id) => {
    const response = await api.post(`/orders/${id}/sync-odoo`);
    return response.data;
  },

  toggleImportant: async (id, isImportant) => {
    const response = await api.patch(`/orders/${id}/important`, { isImportant });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/orders/${id}`);
    return response.data;
  },

  deletePermanent: async (id) => {
    const response = await api.delete(`/orders/${id}?permanent=true`);
    return response.data;
  },

  archive: async (id) => {
    const response = await api.post(`/orders/${id}/archive`);
    return response.data;
  },

  unarchive: async (id) => {
    const response = await api.post(`/orders/${id}/unarchive`);
    return response.data;
  },

  restore: async (id) => {
    const response = await api.post(`/orders/${id}/restore`);
    return response.data;
  },

  getDeleted: async (params = {}) => {
    const response = await api.get('/orders', { params: { ...params, deleted: 'true' } });
    return { orders: response.data.orders || [], total: response.data.total || 0 };
  },

  getCounts: async (params = {}) => {
    const response = await api.get('/orders/counts', { params });
    return response.data.counts || {};
  },

  updateLine: async (orderId, lineId, updateData) => {
    const response = await api.put(`/orders/${orderId}/lines/${lineId}`, updateData);
    return response.data;
  },

  getPDF: async (id) => {
    const response = await api.get(`/orders/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getWorkOrderPDF: async (id) => {
    const response = await api.get(`/orders/${id}/work-order/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getPresupuestoTPVPDF: async (id, opts = {}) => {
    const response = await api.get(`/orders/${id}/presupuesto-tpv${opts.notes ? '?notes=true' : ''}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getTicketTPVPDF: async (id) => {
    const response = await api.post(`/orders/${id}/ticket-tpv`, {}, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// Product services
export const productService = {
  getAll: async (params) => {
    const response = await api.get('/products', { params });
    return response.data.products || [];
  },

  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data.product || response.data;
  }
};

// Delivery Note services
export const deliveryNoteService = {
  getAll: async (params) => {
    const response = await api.get('/delivery-notes', { params });
    return response.data.deliveryNotes || [];
  },

  getById: async (id) => {
    const response = await api.get(`/delivery-notes/${id}`);
    return response.data.deliveryNote || response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.patch(`/delivery-notes/${id}/status`, { status });
    return response.data;
  },

  getPDF: async (id, { showPrices = true } = {}) => {
    const response = await api.get(`/delivery-notes/${id}/pdf`, {
      params: { showPrices: showPrices ? 'true' : 'false' },
      responseType: 'blob'
    });
    return response.data;
  },

  revert: async (id) => {
    const response = await api.post(`/delivery-notes/${id}/revert`);
    return response.data;
  }
};

export const invoiceService = {
  getAll: async (params) => {
    const response = await api.get('/invoices', { params });
    return response.data.invoices || [];
  },

  getById: async (id) => {
    const response = await api.get(`/invoices/${id}`);
    return response.data.invoice || response.data;
  },

  createFromDeliveryLines: async (deliveryLineIds) => {
    const response = await api.post('/invoices/create-from-delivery-lines', { deliveryLineIds });
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.patch(`/invoices/${id}/status`, { status });
    return response.data;
  },

  revert: async (id) => {
    const response = await api.post(`/invoices/${id}/revert`);
    return response.data;
  }
};

// TPV services
export const tpvService = {
  getActiveSession: async () => {
    const response = await api.get('/tpv/session/active');
    return response.data;
  },

  openSession: async () => {
    const response = await api.post('/tpv/session/open');
    return response.data;
  },

  closeSession: async (sessionId) => {
    const response = await api.put(`/tpv/session/${sessionId}/close`);
    return response.data;
  },

  processTransaction: async (orderId, paymentMethod, sessionId = null) => {
    const response = await api.post('/tpv/transaction', {
      orderId,
      paymentMethod,
      sessionId
    });
    return response.data;
  },

  getTransactions: async (params = {}) => {
    const response = await api.get('/tpv/transactions', { params });
    return response.data;
  },

  getTransactionsSummary: async (params = {}) => {
    const response = await api.get('/tpv/transactions/summary', { params });
    return response.data;
  },

  cancelTransaction: async (transactionId, reason = '') => {
    const response = await api.post(`/tpv/transaction/${transactionId}/cancel`, { reason });
    return response.data;
  }
};

// User services (admin only)
export const userService = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data.users || [];
  },

  create: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data.user || response.data;
  },

  update: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data.user || response.data;
  },

  toggleActive: async (id) => {
    const response = await api.patch(`/users/${id}/toggle-active`);
    return response.data.user || response.data;
  },

  resetPassword: async (id, newPassword) => {
    const response = await api.patch(`/users/${id}/reset-password`, { newPassword });
    return response.data;
  }
};

// Consolidated API export
const apiService = {
  // Auth
  login: authService.login,
  logout: authService.logout,
  getCurrentUser: authService.getCurrentUser,
  isAuthenticated: authService.isAuthenticated,
  
  // Calculator
  getProducts: calculatorService.getProducts,
  calculate: calculatorService.calculate,
  saveOrder: calculatorService.saveOrder,
  
  // Clients
  getClients: clientService.getAll,
  getClient: clientService.getById,
  createClient: async (clientData) => {
    const response = await api.post('/clients', clientData);
    const datos = response.data || {};
    const client = datos.client || datos;
    // Cuando el backend NO crea ficha porque ya existe una con ese teléfono/nombre responde
    // 200 + reused + message. Se conservan sobre el cliente para que la pantalla lo avise
    // (antes se descartaban y los formularios decían "creado" aunque no se hubiera creado nada).
    return datos.reused ? { ...client, reused: true, reusedMessage: datos.message } : client;
  },
  updateClient: clientService.update,
  
  // Orders
  getOrders: orderService.getAll,
  getOrder: orderService.getById,
  updateOrderStatus: orderService.updateStatus,
  deleteOrder: orderService.delete,
  generateDeliveryNote: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/delivery-note`);
    return response.data;
  },
  
  // Products (admin)
  getAllProducts: productService.getAll,
  getProduct: productService.getById,
  
  // Reports
  getReports: async (filters) => {
    const response = await api.get('/reports', { params: filters });
    return response.data;
  },
  generateReport: async (filters) => {
    const response = await api.post('/reports/generate', filters);
    return response.data;
  },
  getReportHistory: async () => {
    const response = await api.get('/reports/history');
    return response.data;
  },
  getReportPdf: async (reportId) => {
    const response = await api.get(`/reports/${reportId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
  exportReportExcel: async (filters) => {
    const response = await api.get('/reports/export', { 
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  // TPV
  ...tpvService
};

export default apiService;

// Export the axios instance for direct use
export { api };
